import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Users, Ship, Building, UtensilsCrossed, Heart, Globe, Award, ArrowRight } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/11820065/pexels-photo-11820065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Notre équipe"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
        </div>
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full">
            <p className="font-accent text-accent tracking-wide mb-4">Qui sommes-nous</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Notre Association
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl">
              Une communauté de passionnés de voyage qui partagent leurs découvertes 
              et négocient ensemble les meilleurs avantages.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-accent text-accent tracking-wide mb-2">Notre mission</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                Rendre le voyage accessible et inspirant
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Fondée par des voyageurs passionnés, notre association a pour vocation de 
                  partager notre amour du voyage sous toutes ses formes : croisières sur les 
                  plus beaux océans, séjours dans des hôtels d'exception, découvertes 
                  gastronomiques...
                </p>
                <p>
                  Nous croyons que le voyage est une source d'enrichissement personnel et 
                  d'ouverture sur le monde. C'est pourquoi nous mettons tout en œuvre pour 
                  faciliter l'accès à des expériences de qualité.
                </p>
                <p>
                  En rejoignant notre communauté, vous bénéficiez non seulement de nos 
                  conseils et récits de voyage, mais aussi d'avantages exclusifs négociés 
                  auprès de nos partenaires.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Globe, label: "Destinations", value: "50+" },
                { icon: Ship, label: "Croisières", value: "150+" },
                { icon: Users, label: "Adhérents", value: "500+" },
                { icon: Award, label: "Partenaires", value: "20+" },
              ].map((stat, i) => (
                <div key={i} className="bg-secondary p-6 text-center">
                  <stat.icon className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="font-heading text-3xl font-bold text-primary">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12">
            <p className="font-accent text-accent tracking-wide mb-2">Nos valeurs</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary">
              Ce qui nous anime
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Passion",
                description: "Le voyage est notre passion. Nous la partageons avec enthousiasme et authenticité à travers nos récits et conseils."
              },
              {
                icon: Users,
                title: "Communauté",
                description: "Ensemble, nous sommes plus forts. Notre force collective nous permet de négocier des avantages exclusifs pour tous."
              },
              {
                icon: Award,
                title: "Excellence",
                description: "Nous sélectionnons avec soin nos partenaires et recommandations pour vous garantir des expériences de qualité."
              }
            ].map((value, i) => (
              <div key={i} className="bg-white p-8 border border-border">
                <value.icon className="h-10 w-10 text-accent mb-4" />
                <h3 className="font-heading text-xl font-semibold text-primary mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we offer */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12">
            <p className="font-accent text-accent tracking-wide mb-2">Adhésion</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary">
              Ce que nous offrons
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Ship, title: "Réductions Croisières", desc: "Jusqu'à -15% chez nos partenaires" },
              { icon: Building, title: "Avantages Hôtels", desc: "Tarifs négociés et surclassements" },
              { icon: UtensilsCrossed, title: "Bons Plans Restos", desc: "Offres exclusives" },
              { icon: Globe, title: "Conseils Personnalisés", desc: "Accompagnement voyage" },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-accent" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-primary mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Rejoignez l'aventure
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Devenez membre de notre association et commencez à profiter de tous nos avantages dès aujourd'hui.
          </p>
          <Link to="/adherer">
            <Button className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-base">
              Devenir adhérent
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
